<script setup lang="ts">
import { useGetAdminAtsIntegrationConnectionDetail } from '@swipe4work/api-orval'
import type { GetAdminAtsIntegrationConnectionDetailResult } from '@swipe4work/models-orval'
import { useJobAdapter } from '@/shared/adapters/matching/useJobAdapter'

definePage({
  path: '/companies/:id/integrations/:connectionId',
  name: 'companies.[id].integrations.[connectionId]',
  meta: {
    title: 'Integratie',
    breadcrumbParent: 'companies.overview',
    permission: {
      resource: 'Company',
      level: 'Read',
    },
  },
})

const route = useRoute('companies.[id].integrations.[connectionId]')
const router = useRouter()
const companyId = computed(() => Number.parseInt((route.params as { id: string }).id))
const connectionId = computed(() => Number.parseInt((route.params as { connectionId: string }).connectionId))

const { integrationLabel } = useJobAdapter()

const { data: detail, isPending, isError, refetch } = useGetAdminAtsIntegrationConnectionDetail(connectionId)

provide<Ref<GetAdminAtsIntegrationConnectionDetailResult | undefined>>('integrationDetail', detail)
provide<Ref<number>>('integrationConnectionId', connectionId)
provide<() => void>('reloadIntegrationDetail', () => refetch())

function redirectToOverview() {
  router.replace({
    name: 'companies.[id].integrations.[connectionId].overview' as any,
    params: {
      id: companyId.value,
      connectionId: connectionId.value, 
    },
  })
}

onBeforeMount(() => {
  if (route.name === 'companies.[id].integrations.[connectionId]') {
    redirectToOverview()
  }
})

watch(() => route.name, (name) => {
  if (name === 'companies.[id].integrations.[connectionId]') {
    redirectToOverview()
  }
})

const tabs = [
  {
    name: 'companies.[id].integrations.[connectionId].overview',
    label: 'Overzicht',
  },
  {
    name: 'companies.[id].integrations.[connectionId].ats-jobs',
    label: 'ATS vacatures',
  },
  {
    name: 'companies.[id].integrations.[connectionId].applications',
    label: 'Sollicitaties',
  },
  {
    name: 'companies.[id].integrations.[connectionId].errors',
    label: 'Foutmeldingen',
  },
]
</script>

<template>
  <div class="flex flex-col gap-5 p-6">
    <div class="flex items-center gap-3">
      <RouterLink
        :to="{ name: 'companies.[id]', params: { id: companyId } }"
        class="text-sm text-surface-500 hover:underline"
      >
        <i class="fa-regular fa-arrow-left" /> Terug naar bedrijf
      </RouterLink>
    </div>

    <div v-if="isPending" class="flex flex-col gap-2">
      <Skeleton height="2rem" width="16rem" />
      <Skeleton height="12rem" />
    </div>
    <Message v-else-if="isError || !detail" severity="error">
      Er is iets misgegaan bij het laden van deze integratie.
    </Message>
    <template v-else>
      <h3 class="m-0 text-lg text-surface-900 font-bold">
        {{ integrationLabel(detail.type) }}
      </h3>

      <section>
        <Tabs :value="route.name" scrollable>
          <TabList>
            <router-link
              v-for="tab in tabs"
              :key="tab.name"
              :to="{ name: tab.name as any, params: { id: companyId, connectionId } }"
            >
              <Tab :value="tab.name">
                {{ tab.label }}
              </Tab>
            </router-link>
          </TabList>
        </Tabs>

        <router-view v-slot="{ Component }">
          <component :is="Component" />
        </router-view>
      </section>
    </template>
  </div>
</template>
